// Dependencies
import React from 'react'
import _ from 'lodash'
import { AppContext } from '../contexts/AppContext'
import styled, { css } from 'styled-components'

// Types
import { Types, Containers } from '../types'

// Hooks 
import { useDebug } from '../hooks'
import Product from './ProductView'
import { Availability, AvailabilityText, AvailabilityWrapper, CurrentPrice, DeliveryTime, Price, PriceWrapper, StockStatus } from '../components/Product'
import { Helpers } from '../utils'
import { ProductVariableSelectWrapper } from './ProductSelect'

const BundledProductItemWrapper = styled.div(
  ({ theme: { general, components: { productAttributes: style } } }) => css`
    ${({ theme: { general } }) => Helpers.clamp('padding', general.wrapper.padding.inline / 3, general.wrapper.padding.inline, 2.5)}
    ${({ theme: { general } }) => Helpers.clamp('padding-top', general.wrapper.padding.block / 2.5, general.wrapper.padding.block, 2.5)}
    ${({ theme: { general } }) => Helpers.clamp('padding-bottom', general.wrapper.padding.inline / 2.5, general.wrapper.padding.inline, 2.5)}
    ${ProductVariableSelectWrapper} {
      padding: 0;
      margin-top: 1.5em;
      margin-bottom: 0;
      padding-top: 1.5em;
      border-top: 1px #ddd solid;
      background: transparent !important;
    }
    transform: translateY(0);
    transition: all ${general.transition.duration} ${general.transition.timingFunction};

    background: #fafafa;
          
    outline: none;

    // TODO: Mixin
    &:hover, :focus-within {
      background: #f5f5f5;
    }
    
    @media (-moz-touch-enabled: 1), (hover: none), (pointer: coarse) {
        &:hover {
          transform: translateY(0);
          background: #fafafa;
        }
        &:focus-within {
            transform: none !important;
            box-shadow: unset !important;
        }
        &:active {
          background: #f5f5f5;
        }
    }
`);

const BundledProductImage = styled.img(
  ({ theme: { general, components: { productAttributes: style } } }) => css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    border-radius: 0.25em;
    mix-blend-mode: multiply; // TODO: Check how images with white background renders (and also consider that background could be set to any other color)
  `);

const BundledProductLink = styled.a<any>(
  ({ wrapping = '', theme: { general, components: { productAttributes: style } } }) => css`
    text-decoration: none;
    color: currentColor !important;
    display: flex;
    flex-direction: column;
    position: relative;
    width: fit-content;
    height: auto;
    &:active, &:hover, &::selection {
      background: transparent !important;
      color: currentColor !important;
    }
    ${wrapping === 'image' && `
      margin-right: 0.5em;
      ${Helpers.clamp('width', 3.5, 6, 5)};
      ${Helpers.clamp('height', 3.5, 6, 5)};
      ${Helpers.clamp('min-width', 3.5, 6, 5)};
      ${Helpers.clamp('min-height', 3.5, 6, 5)};
    `}
    outline: none;
  `);

const BundledProductName = styled.h3(
  ({ theme: { general, components: { productAttributes: style } } }) => css`
    margin: 0;
    display: flex;
    align-items: center
    ${BundledProductQuantity} {
      margin-left: 1em;
    }
    ${Helpers.clamp('font-size', 0.85, 1.2, 2.5)};
  `);

const BundledProductDescription = styled.p(
  ({ theme: { general, components: { productAttributes: style } } }) => css`
      margin: 0.35em 0 0;
      ${Helpers.clamp('font-size', 0.75, 0.9, 2.5)};
      color: #868686;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      hyphens: auto;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
  `);

const BundledProductQuantity = styled.span(
  ({ theme: { general, components: { productAttributes: style } } }) => css`
    // width: 100%;
    // border-radius: 0 0 0.15em 0.15em;
    width: fit-content;
    position: absolute;
    border-radius: 0.15em;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    bottom: -0.5em;
    right: -0.5em;
    height: 1.5em;
    margin: 0;
    ${Helpers.clamp('font-size', 0.75, 0.85, 2.5)};
    color: #fff;
    background: #000000;
    padding: 0.625em;
  `);

const BundledProductInfo = styled.div<any>(
  ({ decimals, theme: { general, components: { productAttributes: style } } }) => css`
    display: flex;
    line-height: 1.25;
    align-items: center;
    ${Helpers.clamp('column-gap', 0.75, 1.25, 2.5)};
    ${PriceWrapper} {
      font-size: 50%;
      width: fit-content;
    }
    ${CurrentPrice} {                //             3  1 2  1
      min-width: ${3 + 0.25 + 1 + decimals}ch; // Space for [000].[00]€
    }
  `);

const BundledProductMeta = styled.div(
  ({ theme: { general, components: { productAttributes: style } } }) => css`
    display: flex;
    flex-direction: column;
    ${PriceWrapper} {
      font-size: 70%;
    }
    ${AvailabilityWrapper} {
      margin-bottom: 0.5em;
      font-size: 60%;
      @media (min-width: ${Helpers.getBreakpoint('mobile', 'md')}) {
        flex-direction: row;
        align-items: center;
      }
      svg {
        width: 1.25em;
        height: 1.25em;
      }
      ${AvailabilityText} {
        align-items: center;
        flex-direction: row;
        flex-wrap: wrap;
        ${StockStatus} {
          margin-right: 0.5em;
        }
      }
    }
  `);

const BundledProductItem = ({ product, bundleProduct, ...props }: any) => {

  useDebug(`BundledProductItem [${bundleProduct.name}/${product.id}]`)

  // TODO: Refactor
  // TODO: Avoid unnecessary updates (per each item)
  // NOTE: Temp solution I guess (2x updates)

  const getProductPrice = React.useCallback((price: any) => {
    const productPrice = { ...price };

    productPrice.regular = price?.sale ?? price?.regular; // Original product's price
    productPrice.sale = productPrice?.regular * bundleProduct.saleRatio ;

    return productPrice;
  }, [bundleProduct.saleRatio])

  const getProductQuantity = React.useCallback((quantity) => {
    const newQuantity = { ...quantity };
    // NOTE: Assuming it will not reach max quantity of itself as parent's Bundle product stock should be equal the lowest of available
    newQuantity.selected = (newQuantity?.selected || newQuantity?.min || props.config.general.quantity.min) * bundleProduct.quantity.selected;
    return newQuantity;
  }, [bundleProduct.quantity.selected, props.config.general.quantity.min]);

  // TODO: Refactor, optimise
  const getCurrentProduct = React.useCallback(() => { 

    const currentProduct = { ...product }; // a.k.a. currentBundleItem

    if ( currentProduct.type === 'variable' ) {
      const currentVariation =
        ((currentProduct.id in bundleProduct.selected) && (bundleProduct.selected[currentProduct.id].variation && (bundleProduct.selected[currentProduct.id].variation in currentProduct?.variations)))
          ? currentProduct?.variations[bundleProduct.selected[currentProduct.id].variation]
          : undefined;

      // Update some fields when variation is selected
      // NOTE: Maybe filter out meta like availability
      // NOTE: Stock status will also display outofstock, 
      //       When that happens,we expect to bundle be out of stock initially 
      //      (and as we hiding out of stock variations for now, there is no need to updated dynamically Bundle product availability for now)
      if (currentVariation) {

        Object.entries(currentVariation).forEach(([name, value]: any) => {
          // Skip certain fields which should stay the same
          if (['variations', 'permalink', 'name'].includes(name)) return;
          currentProduct[name] = value ?? ((name in currentProduct) ? currentProduct[name] : undefined);
        })

        // NOTE: Two variations - description as selected atts or original variation description + selected atts
        // currentProduct.description = `${currentProduct.name} (${Object.values(currentVariation.attributes).join(', ')})`;
        currentProduct.description = `${Object.values(currentVariation.attributes).join(', ')}`;

    }

    }

    // Special cases which needs addtional handlng
    currentProduct.price = getProductPrice(currentProduct.price);
    currentProduct.quantity = getProductQuantity(currentProduct.quantity);

    return currentProduct;

  }, [bundleProduct.selected, product, getProductPrice, getProductQuantity])

  const { permalink, image, name, description, price, quantity, ...productMeta } = getCurrentProduct();

  return (
    <BundledProductItemWrapper>
      <BundledProductInfo {...props.config.general}>
        <BundledProductLink wrapping="image" target="_blank" href={permalink}>
          <BundledProductImage src={image.feature ?? props.config.general.placeholderImage} alt={`Product ${name}`} />
          <BundledProductQuantity>
            <span>{quantity?.selected}</span>
            <span>&#8202;{props.translations.unit.abbr}</span>
          </BundledProductQuantity>
        </BundledProductLink>
        <BundledProductLink tabIndex={-1} target="_blank" href={permalink}>
          <BundledProductMeta>
            <Availability {...{ product: productMeta, ...props }} />
            <BundledProductName>
              {name}
            </BundledProductName>
            <BundledProductDescription>{description}</BundledProductDescription>
          </BundledProductMeta>
        </BundledProductLink>
        <Price {...{ price, quantity: quantity.selected, ...props }} />
      </BundledProductInfo>
      <Product {...{ ...props, product }} /> 
    </BundledProductItemWrapper>
  );

}
export default BundledProductItem;